import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Newspaper, RefreshCw, ExternalLink, TrendingUp, Plane, Building2, MapPin, Shield, X, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  market: TrendingUp,
  airline: Plane,
  hotel: Building2,
  destination: MapPin,
  policy: Shield,
};

const categoryColors = {
  market: "bg-blue-50 text-blue-600 border-blue-100",
  airline: "bg-sky-50 text-sky-600 border-sky-100",
  hotel: "bg-purple-50 text-purple-600 border-purple-100",
  destination: "bg-emerald-50 text-emerald-600 border-emerald-100",
  policy: "bg-amber-50 text-amber-600 border-amber-100",
};

const getDismissKey = () => `dismissed_news_${new Date().toISOString().split('T')[0]}`;
const getSectionDismissKey = () => `dismissed_news_section_${new Date().toISOString().split('T')[0]}`;

const TravelNews = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(getDismissKey()) || '[]'); } catch { return []; }
  });
  const [sectionDismissed, setSectionDismissed] = useState(() => {
    try { return sessionStorage.getItem(getSectionDismissKey()) === 'true'; } catch { return false; }
  });

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 7200000);
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/travel-news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const dismissItem = (idx) => {
    const updated = [...dismissed, idx];
    setDismissed(updated);
    sessionStorage.setItem(getDismissKey(), JSON.stringify(updated));
  };

  const dismissSection = () => {
    setSectionDismissed(true);
    sessionStorage.setItem(getSectionDismissKey(), 'true');
  };

  const getNewsPagePath = () => {
    const role = user?.role;
    if (role === 'admin' || role === 'employee') return '/admin/travel-news';
    return '/agent/travel-news';
  };

  if (loading && !news) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-testid="travel-news-loading">
        <div className="flex items-center gap-2 mb-3">
          <Newspaper className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-gray-700">AI Travel News</span>
        </div>
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-20 bg-gray-50 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!news || (!news.ai_insights?.length && !news.latest_headlines?.length)) return null;

  if (sectionDismissed) return null;

  // Filter out dismissed insights
  const visibleInsights = (news.ai_insights || []).filter((_, i) => !dismissed.includes(`insight-${i}`));
  const visibleHeadlines = (news.latest_headlines || []).filter((_, i) => !dismissed.includes(`headline-${i}`));

  if (visibleInsights.length === 0 && visibleHeadlines.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4" data-testid="travel-news">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-gray-700">AI Travel News</span>
          </div>
          <button onClick={() => navigate(getNewsPagePath())} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
            View All <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <p className="text-sm text-gray-400 text-center mt-4 mb-2">All news dismissed for today</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="travel-news">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Newspaper className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-gray-700">AI Travel News</span>
          <span className="text-[10px] text-white bg-emerald-500 px-2 py-0.5 rounded-full font-medium">AI Curated</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(getNewsPagePath())}
            className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            data-testid="view-all-news-btn"
          >
            View All <ArrowRight className="w-3 h-3" />
          </button>
          <button
            onClick={fetchNews}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-emerald-600 transition-colors"
            title="Refresh news"
            data-testid="refresh-news-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={dismissSection}
            className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
            title="Dismiss news section"
            data-testid="dismiss-news-section-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* AI Insights */}
      <div className="p-4">
        <div className="space-y-3" data-testid="ai-insights">
          {visibleInsights.slice(0, 4).map((insight, actualIdx) => {
            const originalIdx = news.ai_insights.indexOf(insight);
            const cat = insight.category || "market";
            const Icon = categoryIcons[cat] || TrendingUp;
            const colors = categoryColors[cat] || categoryColors.market;
            return (
              <div
                key={originalIdx}
                className="group relative flex gap-3 p-3 rounded-lg bg-gray-50/50 hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100 cursor-pointer"
                data-testid={`insight-${originalIdx}`}
                onClick={() => {
                  if (insight.link) window.open(insight.link, '_blank');
                }}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${colors}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-800 leading-snug">{insight.headline}</h4>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{insight.summary}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); dismissItem(`insight-${originalIdx}`); }}
                  className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                  title="Dismiss"
                  data-testid={`dismiss-insight-${originalIdx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      {news.updated_at && (
        <div className="px-4 py-2 border-t border-gray-50 bg-gray-50/50">
          <p className="text-[10px] text-gray-400 text-right">
            Updated: {new Date(news.updated_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} | Sources: Skift, BTN, TTG Asia, HospitalityNet
          </p>
        </div>
      )}
    </div>
  );
};

export default TravelNews;
